import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

/* The service answers with a REAL HTTP status and a { code, message } body, so
   axios rejects and the sentence is read off the error. A bare refusal
   ({ code: "forbidden" }) carries no message, which is why the code is the
   second thing tried. */
const errText = (err, fallback) =>
  err.response?.data?.message || err.response?.data?.code || fallback;

/* ═══════════════════════════════════════════════════════════════
   THERE IS NO STOCK-ENTRY DOCUMENT ANY MORE.

   Node kept one StockEntry per product with an entries[] history inside
   it. Postgres keeps a BIN — grams on hand per (item, store) in
   stock.TrnBalances — plus an append-only move log, and the bulk pool is
   addressed by PRODUCT id through /admin/bulk/grains/*. Quantities are
   GRAMS on the wire; the MRP and selling price are RUPEES PER KG (the
   server converts them to paise) and the figures that come back are
   *Paise / *Grams, so anything rendered from them has to be scaled.
═══════════════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════════════════════
   FETCH ALL BULK GRAINS
   GET /admin/bulk/grains
   A superadmin must name a storeId or the bin figures come back null —
   a chain total of bins in different towns is true of nothing. A
   store-bound admin is pinned to its own store and storeId is ignored.
═══════════════════════════════════════════════════════════════ */
export const fetchStockEntries = createAsyncThunk(
  "stockEntry/fetchAll",
  async ({ storeId = "", q = "", limit = 100, offset = 0 } = {}, thunkAPI) => {
    try {
      /* the list is an offset window, not a numbered page — the route takes
         limit/offset and returns no total */
      let url = `/admin/bulk/grains?limit=${limit}&offset=${offset}`;
      if (storeId) url += `&storeId=${encodeURIComponent(storeId)}`;
      if (q)       url += `&q=${encodeURIComponent(q)}`;
      const res = await api.get(url);
      return res.data; // { scope, grains, limit, offset }
    } catch (err) {
      return thunkAPI.rejectWithValue(errText(err, "Failed to fetch stock entries"));
    }
  }
);

/* ═══════════════════════════════════════════════════════════════
   FETCH ONE GRAIN + ITS MOVE LOG
   GET /admin/bulk/grains/:productId
   The moves[] here are what replaced StockEntry.entries[].
═══════════════════════════════════════════════════════════════ */
export const fetchStockByProduct = createAsyncThunk(
  "stockEntry/fetchByProduct",
  async ({ productId, storeId = "", limit = 50 }, thunkAPI) => {
    try {
      let url = `/admin/bulk/grains/${productId}?limit=${limit}`;
      if (storeId) url += `&storeId=${encodeURIComponent(storeId)}`;
      const res = await api.get(url);
      return res.data; // { scope, grain, moves }
    } catch (err) {
      return thunkAPI.rejectWithValue(errText(err, "Failed to fetch stock for product"));
    }
  }
);

/* ═══════════════════════════════════════════════════════════════
   FETCH STOCK ENTRY BY ID
═══════════════════════════════════════════════════════════════ */
export const fetchStockEntryById = createAsyncThunk(
  "stockEntry/fetchById",
  async (id, thunkAPI) => {
    try {
      // TODO(cutover): no server endpoint for this
      /* Nothing is addressable by a stock-entry id any more, because the
         document it named does not exist: the bulk pool is keyed by PRODUCT
         (GET /admin/bulk/grains/{productId}) and the history is the move log.
         Left on the old path rather than dropped, so the feature fails loudly
         instead of disappearing. */
      const res = await api.get(`/api/stockentries/${id}`);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(errText(err, "Failed to fetch stock entry"));
    }
  }
);

/* ═══════════════════════════════════════════════════════════════
   OPENING STOCK — an absolute recount
   POST /admin/bulk/grains/:productId/opening
   Body: { quantity (GRAMS, 0 allowed), mrp, sellingPrice (RUPEES PER KG,
   both required), store?, addedDate?, note?, reference?, costPerKgPaise?,
   idempotencyKey? }.  `createdBy` is gone — the actor comes from the token.
   The server posts the DIFFERENCE against the opening counterparty; the
   balance stays one row and the history is the move log.
═══════════════════════════════════════════════════════════════ */
export const addOpeningStock = createAsyncThunk(
  "stockEntry/addOpening",
  async ({ productId, ...body }, thunkAPI) => {
    try {
      const res = await api.post(`/admin/bulk/grains/${productId}/opening`, body);
      return res.data; // receipt: { ok, productId, beforeGrams, afterGrams, … }
    } catch (err) {
      return thunkAPI.rejectWithValue(errText(err, "Failed to set opening stock"));
    }
  }
);

/* ═══════════════════════════════════════════════════════════════
   RESTOCK — grams in, at the batch's price
   POST /admin/bulk/grains/:productId/restock
   Same body as opening, except the quantity must be above 0.
═══════════════════════════════════════════════════════════════ */
export const restockProduct = createAsyncThunk(
  "stockEntry/restock",
  async ({ productId, ...body }, thunkAPI) => {
    try {
      const res = await api.post(`/admin/bulk/grains/${productId}/restock`, body);
      return res.data; // receipt, not a grain row
    } catch (err) {
      return thunkAPI.rejectWithValue(errText(err, "Failed to restock product"));
    }
  }
);

/* ═══════════════════════════════════════════════════════════════
   DELETE ENTRY LINE
═══════════════════════════════════════════════════════════════ */
export const deleteEntryLine = createAsyncThunk(
  "stockEntry/deleteEntryLine",
  async ({ stockEntryId, entryLineId }, thunkAPI) => {
    try {
      // TODO(cutover): no server endpoint for this
      /* stock.TrnMoves is append-only — a move is corrected by posting the
         opposite move, and there is no DELETE anywhere under /stock or
         /admin/bulk. Left on the old path rather than dropped. */
      const res = await api.delete(
        `/api/stockentries/${stockEntryId}/entry/${entryLineId}`
      );
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(errText(err, "Failed to delete entry line"));
    }
  }
);

/* ═══════════════════════════════════════════════════════════════
   DELETE STOCK ENTRY
═══════════════════════════════════════════════════════════════ */
export const deleteStockEntry = createAsyncThunk(
  "stockEntry/delete",
  async (id, thunkAPI) => {
    try {
      // TODO(cutover): no server endpoint for this
      /* There is no per-product stock record to remove: a bin is a balance
         row that exists for as long as the item does. Zeroing it is an
         opening recount to 0, which is a different operation and is NOT
         substituted here. Left on the old path rather than dropped. */
      await api.delete(`/api/stockentries/${id}`);
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue(errText(err, "Failed to delete stock entry"));
    }
  }
);

/* ═══════════════════════════════════════════════════════════════
   SLICE
═══════════════════════════════════════════════════════════════ */
const stockEntrySlice = createSlice({
  name: "stockEntry",
  initialState: {
    loading:      false,
    error:        null,
    success:      false,

    stockEntries:     [],   // list view — one row per bulk grain
    pagination:       {},   // { limit, offset } — the route has no total
    scope:            null, // the store the figures belong to; null = none named

    currentEntry:     null, // { scope, grain, moves } for one product
    lastWrite:        null, // the receipt from the last opening/restock
  },
  reducers: {
    resetStockEntryState: (state) => {
      state.loading = false;
      state.error   = null;
      state.success = false;
    },
    clearCurrentEntry: (state) => {
      state.currentEntry = null;
    },
  },
  extraReducers: (builder) => {
    builder

      /* ── fetchAll ── */
      .addCase(fetchStockEntries.pending,   (state)         => { state.loading = true; state.error = null; })
      .addCase(fetchStockEntries.fulfilled, (state, action) => {
        state.loading      = false;
        state.stockEntries = action.payload.grains || [];
        state.pagination   = { limit: action.payload.limit, offset: action.payload.offset };
        state.scope        = action.payload.scope ?? null;
      })
      .addCase(fetchStockEntries.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      /* ── fetchByProduct ── */
      .addCase(fetchStockByProduct.pending,   (state)         => { state.loading = true; state.currentEntry = null; })
      .addCase(fetchStockByProduct.fulfilled, (state, action) => {
        state.loading      = false;
        state.currentEntry = action.payload;
      })
      .addCase(fetchStockByProduct.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      /* ── fetchById ── */
      .addCase(fetchStockEntryById.pending,   (state)         => { state.loading = true; state.currentEntry = null; })
      .addCase(fetchStockEntryById.fulfilled, (state, action) => {
        state.loading      = false;
        state.currentEntry = action.payload;
      })
      .addCase(fetchStockEntryById.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      /* ── addOpeningStock ──
         The write answers with a RECEIPT (ok, beforeGrams, afterGrams,
         pricePerKgPaise…), not with the grain row it changed, so there is
         nothing to splice into the list — refetch on success instead. */
      .addCase(addOpeningStock.pending,   (state)         => { state.loading = true; state.error = null; state.success = false; })
      .addCase(addOpeningStock.fulfilled, (state, action) => {
        state.loading   = false;
        state.success   = true;
        state.lastWrite = action.payload;
      })
      .addCase(addOpeningStock.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      /* ── restockProduct ── */
      .addCase(restockProduct.pending,   (state)         => { state.loading = true; state.error = null; state.success = false; })
      .addCase(restockProduct.fulfilled, (state, action) => {
        state.loading   = false;
        state.success   = true;
        state.lastWrite = action.payload;
      })
      .addCase(restockProduct.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      /* ── deleteEntryLine ── */
      .addCase(deleteEntryLine.pending,   (state)         => { state.loading = true; state.error = null; })
      .addCase(deleteEntryLine.fulfilled, (state, action) => {
        state.loading      = false;
        state.currentEntry = action.payload;
      })
      .addCase(deleteEntryLine.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      /* ── deleteStockEntry ── */
      .addCase(deleteStockEntry.pending,   (state)         => { state.loading = true; state.error = null; })
      .addCase(deleteStockEntry.fulfilled, (state, action) => {
        state.loading      = false;
        /* a grain row is keyed by `id` (the product id) — there is no _id */
        state.stockEntries = state.stockEntries.filter((e) => e.id !== action.payload);
        if (state.currentEntry?.grain?.id === action.payload) state.currentEntry = null;
      })
      .addCase(deleteStockEntry.rejected,  (state, action) => { state.loading = false; state.error = action.payload; });
  },
});

export const { resetStockEntryState, clearCurrentEntry } = stockEntrySlice.actions;
export default stockEntrySlice.reducer;
